import React, { useState } from 'react';
import { decidePipelineProposal, undoPipelineProposal } from '../services/pipelineProposals';

/**
 * A proposed pipeline change, and the decision about it.
 *
 * Every line here is rendered from the operations the server validated — not
 * from anything the model wrote about them. That is the point: Buzz's context
 * includes text from other apps and public forms, so a prose summary is
 * something that can be steered. A list built from the operations cannot
 * describe one change and perform another.
 */
export default function PipelineProposalCard({ proposal, workspaceId, onApplied }) {
  const [state, setState] = useState(proposal.ok ? 'proposed' : 'invalid');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const decide = async (decision) => {
    setBusy(true); setError('');
    const res = await decidePipelineProposal(workspaceId, proposal.id, decision);
    setBusy(false);
    if (!res.success) { setError(res.error || 'That did not work.'); setState(res.status || state); return; }
    setState(res.status);
    if (res.applied) onApplied?.();
  };

  const undo = async () => {
    setBusy(true); setError('');
    const res = await undoPipelineProposal(workspaceId, proposal.id);
    setBusy(false);
    if (!res.success) { setError(res.error || 'Could not undo that.'); return; }
    setState('undone');
    onApplied?.();
  };

  if (proposal.cannotDo) {
    return (
      <div className="proposal-card cannot">
        <div className="proposal-head">Not something I can change</div>
        <p className="proposal-intent">{proposal.cannotDo}</p>
        {proposal.suggestion && (
          <p className="proposal-suggestion">Try: {proposal.suggestion}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`proposal-card ${state}`}>
      <div className="proposal-head">
        {state === 'applied' ? '✓ Change applied'
          : state === 'rejected' ? 'Change rejected'
          : state === 'undone' ? '↩ Change undone'
          : state === 'stale' ? '⚠ No longer fits'
          : state === 'invalid' ? '⚠ Buzz proposed something that will not work'
          : 'Buzz proposes this change'}
      </div>

      {proposal.intent && <p className="proposal-intent">{proposal.intent}</p>}

      <ol className="proposal-ops">
        {(proposal.describe || []).map((line, i) => <li key={i}>{line}</li>)}
      </ol>

      {proposal.suggestion && state === 'proposed' && (
        <p className="proposal-suggestion">Next: {proposal.suggestion}</p>
      )}

      {(proposal.warnings || []).length > 0 && (
        <ul className="proposal-warnings">
          {proposal.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}

      {(proposal.errors || []).length > 0 && (
        <ul className="proposal-errors">
          {proposal.errors.map((e, i) => <li key={i}>✗ {e}</li>)}
        </ul>
      )}

      {error && <div className="proposal-errors"><li>✗ {error}</li></div>}

      {state === 'proposed' && (
        <div className="proposal-actions">
          <button className="proposal-approve" disabled={busy} onClick={() => decide('approved')}>
            {busy ? 'Applying…' : 'Approve'}
          </button>
          <button className="proposal-reject" disabled={busy} onClick={() => decide('rejected')}>
            Reject
          </button>
          {/* Said plainly rather than in a tooltip: approving writes to the
              pipeline everything else reads from. */}
          <span className="proposal-note">Approving changes your live pipeline. You can undo it.</span>
        </div>
      )}

      {state === 'applied' && (
        <div className="proposal-actions">
          <button className="proposal-undo" disabled={busy} onClick={undo}>
            {busy ? 'Undoing…' : 'Undo this change'}
          </button>
        </div>
      )}
    </div>
  );
}
